/**
 * GraphQL Query Definitions
 *
 * Contains all GraphQL queries and mutations used by Apollo Client hooks.
 *
 * Feature: 063-graphql-frontend
 */

import { gql } from '@apollo/client';

/**
 * Collection Queries
 */

export const GET_COLLECTIONS = gql`
  query GetCollections($limit: Int, $offset: Int) {
    collections(limit: $limit, offset: $offset) {
      items {
        id
        globalId
        title
        name
        slug
        description
        showExif
        isPublic
        isShared
        photoCount
        heroPhotoId
        heroStyle
        heroPhoto {
          id
          globalId
          title
          slug
          width
          height
          variants {
            id
            variantType
            storageUrl
            publicUrl
            width
            height
          }
        }
        heroImage
        sessions {
          id
          title
          slug
          isActive
          createdAt
        }
        createdAt
        updatedAt
        userId
      }
      totalCount
      hasMore
      limit
      offset
    }
  }
`;

export const GET_COLLECTION = gql`
  query GetCollection($id: ID!) {
    collection(id: $id) {
      id
      globalId
      title
      name
      slug
      description
      showExif
      isPublic
      isShared
      photoCount
      heroPhotoId
      heroStyle
      heroPhoto {
        id
        globalId
        title
        slug
        width
        height
        variants {
          id
          variantType
          storageUrl
          publicUrl
          width
          height
        }
      }
      heroImage
      layout
      sessions {
        id
        title
        slug
        isActive
        createdAt
      }
      createdAt
      updatedAt
      userId
      user {
        id
        globalId
        username
        displayName
        slug
      }
      items(limit: 1000) {
        items {
          id
          globalId
          photoId
          position
          caption
          photo {
            id
            globalId
            title
            slug
            description
            filename
            mimeType
            width
            height
            createdAt
            updatedAt
            userId
            metadata {
              width
              height
              camera
              lens
              iso
              aperture
              shutterSpeed
              focalLength
              dateTaken
            }
            variants {
              id
              variantType
              storageUrl
              publicUrl
              width
              height
              fileSize
            }
          }
        }
        totalCount
      }
    }
  }
`;

/**
 * Photo Queries
 */

export const GET_PHOTOS = gql`
  query GetPhotos($limit: Int, $offset: Int) {
    photos(limit: $limit, offset: $offset) {
      items {
        id
        globalId
        title
        slug
        description
        filename
        mimeType
        width
        height
        createdAt
        updatedAt
        userId
        collections {
          items {
            id
            name
            slug
          }
          totalCount
        }
        metadata {
          width
          height
          camera
          lens
          iso
          aperture
          shutterSpeed
          focalLength
          dateTaken
        }
        variants {
          id
          variantType
          storageUrl
          publicUrl
          width
          height
          fileSize
        }
      }
      totalCount
      hasMore
      limit
      offset
    }
  }
`;

export const GET_PHOTO = gql`
  query GetPhoto($id: ID!) {
    photo(id: $id) {
      id
      globalId
      title
      slug
      description
      filename
      mimeType
      width
      height
      fileHash
      createdAt
      updatedAt
      userId
      collections {
        items {
          id
          name
          slug
        }
        totalCount
      }
      metadata {
        width
        height
        camera
        lens
        iso
        aperture
        shutterSpeed
        focalLength
        dateTaken
      }
      variants {
        id
        variantType
        storageUrl
        publicUrl
        width
        height
        fileSize
      }
      user {
        id
        globalId
        username
        displayName
        slug
      }
    }
  }
`;

export const GET_PHOTO_BY_SLUG = gql`
  query GetPhotoBySlug($username: String!, $photoSlug: String!) {
    userPhotoBySlug(username: $username, photoSlug: $photoSlug) {
      id
      globalId
      title
      slug
      description
      filename
      mimeType
      width
      height
      createdAt
      updatedAt
      userId
      metadata {
        width
        height
        camera
        lens
        iso
        aperture
        shutterSpeed
        focalLength
        dateTaken
        latitude
        longitude
      }
      variants {
        id
        variantType
        storageUrl
        publicUrl
        width
        height
        fileSize
      }
    }
  }
`;

/**
 * User Queries
 */

export const GET_USER_BY_USERNAME = gql`
  query GetUserByUsername($username: String!) {
    userByUsername(username: $username) {
      id
      globalId
      username
      displayName
      slug
      createdAt
      updatedAt
    }
  }
`;

/**
 * Get user profile with photos and collections
 */
export const GET_USER_PROFILE = gql`
  query GetUserProfile($username: String!, $photosLimit: Int, $photosOffset: Int, $collectionsLimit: Int, $collectionsOffset: Int) {
    userByUsername(username: $username) {
      id
      globalId
      username
      displayName
      slug
      createdAt
      updatedAt
      photos(limit: $photosLimit, offset: $photosOffset) {
        items {
          id
          globalId
          title
          slug
          description
          filename
          mimeType
          width
          height
          createdAt
          updatedAt
          userId
          metadata {
            width
            height
            camera
            lens
            iso
            aperture
            shutterSpeed
            focalLength
            dateTaken
          }
          variants {
            id
            variantType
            storageUrl
            publicUrl
            width
            height
            fileSize
          }
        }
        totalCount
        hasMore
      }
      collections(limit: $collectionsLimit, offset: $collectionsOffset) {
        items {
          id
          globalId
          title
          name
          slug
          description
          showExif
          isPublic
          isShared
          photoCount
          heroPhotoId
          heroPhoto {
            id
            globalId
            title
            slug
            width
            height
            variants {
              id
              variantType
              storageUrl
              publicUrl
              width
              height
            }
          }
          heroImage
          createdAt
          updatedAt
          userId
        }
        totalCount
        hasMore
      }
    }
  }
`;

/**
 * Critique Queries
 */

export const GET_CRITIQUES_FOR_PHOTO = gql`
  query GetCritiquesForPhoto($photoId: ID!, $limit: Int, $offset: Int) {
    critiquesForPhoto(photoId: $photoId, limit: $limit, offset: $offset) {
      items {
        id
        globalId
        content
        photoId
        parentId
        editId
        sessionId
        createdAt
        updatedAt
        userId
        # Feature 081: Photo version for attachment rendering
        photoVersionId
        photoVersion
        user {
          id
          globalId
          username
          displayName
          slug
        }
      }
      totalCount
      hasMore
      limit
      offset
    }
  }
`;

export const GET_CRITIQUES_FOR_COLLECTION = gql`
  query GetCritiquesForCollection($collectionId: ID!, $sessionId: ID, $limit: Int, $offset: Int) {
    critiquesForCollection(collectionId: $collectionId, sessionId: $sessionId, limit: $limit, offset: $offset) {
      items {
        id
        globalId
        content
        photoId
        parentId
        editId
        sessionId
        createdAt
        updatedAt
        userId
        # Feature 081: Photo version for attachment rendering
        photoVersionId
        photoVersion
        user {
          id
          globalId
          username
          displayName
          slug
        }
        edits {
          id
          editType
          edits
        }
      }
      totalCount
      hasMore
      limit
      offset
    }
  }
`;

export const GET_CRITIQUE_EDITS = gql`
  query GetCritiqueEdits($critiqueId: ID!, $limit: Int, $offset: Int) {
    critiqueEdits(critiqueId: $critiqueId, limit: $limit, offset: $offset) {
      items {
        id
        globalId
        critiqueId
        parentEditId
        editType
        edits
        createdAt
        updatedAt
      }
      totalCount
      hasMore
      limit
      offset
    }
  }
`;

/**
 * Critique Session Queries
 */

export const GET_CRITIQUE_SESSIONS = gql`
  query GetCritiqueSessions($limit: Int, $offset: Int) {
    critiqueSessions(limit: $limit, offset: $offset) {
      items {
        id
        globalId
        title
        slug
        isActive
        isAiSession
        collectionId
        createdAt
        updatedAt
        collection {
          id
          globalId
          title
          name
          slug
          photoCount
          heroPhotoId
          heroPhoto {
            id
            globalId
            description
            variants {
              id
              variantType
              storageUrl
              publicUrl
              width
              height
              fileSize
            }
          }
          user {
            id
            username
            displayName
          }
        }
        critiques {
          items {
            id
            globalId
            content
            photoId
            createdAt
            user {
              id
              username
            }
            photo {
              id
              variants {
                id
                variantType
                storageUrl
                publicUrl
                width
                height
              }
            }
          }
          totalCount
        }
      }
      totalCount
      hasMore
      limit
      offset
    }
  }
`;

export const GET_CRITIQUE_SESSION = gql`
  query GetCritiqueSession($id: ID!) {
    critiqueSession(id: $id) {
      id
      globalId
      title
      slug
      description
      isActive
      intent
      isAiSession
      isPaused
      aiFeedbackStatus {
        pending
        completed
        failed
        total
        isComplete
      }
      # Feature 649: Feedback options
      feedbackOptions {
        enabled
        hasGeneral
        hasRating
      }
      collectionId
      createdAt
      updatedAt
      collection {
        id
        globalId
        title
        name
        slug
        photoCount
        heroPhotoId
        heroPhoto {
          id
          variants {
            id
            variantType
            storageUrl
            publicUrl
            width
            height
          }
        }
      }
      critiques {
        items {
          id
          globalId
          content
          photoId
          parentId
          editId
          createdAt
          updatedAt
          userId
          user {
            id
            globalId
            username
            displayName
            slug
          }
        }
        totalCount
      }
    }
  }
`;

/**
 * Recent Critique Sessions Query
 * Feature: 067-recent-critiques
 */
export const GET_RECENT_CRITIQUE_SESSIONS = gql`
  query GetRecentCritiqueSessions($limit: Int, $offset: Int) {
    recentCritiqueSessions(limit: $limit, offset: $offset) {
      items {
        id
        globalId
        title
        slug
        isActive
        isAiSession
        collectionId
        createdAt
        updatedAt
        collection {
          id
          globalId
          title
          name
          slug
          photoCount
          heroPhotoId
          heroPhoto {
            id
            globalId
            description
            variants {
              id
              variantType
              storageUrl
              publicUrl
              width
              height
              fileSize
            }
          }
          user {
            id
            username
            displayName
          }
        }
        critiques {
          items {
            id
            globalId
            content
            photoId
            createdAt
            user {
              id
              username
            }
            photo {
              id
              variants {
                id
                variantType
                storageUrl
                publicUrl
                width
                height
              }
            }
          }
          totalCount
        }
      }
      totalCount
      hasMore
      limit
      offset
    }
  }
`;

/**
 * Session Contributors Query
 * Gets photographer, critics, and lurkers for a critique session
 */
export const GET_SESSION_CONTRIBUTORS = gql`
  query GetSessionContributors($sessionId: ID!) {
    sessionContributors(sessionId: $sessionId) {
      photographer {
        id
        username
        displayName
        avatarUrl
      }
      critics {
        id
        username
        displayName
        avatarUrl
      }
      lurkers {
        id
        username
        displayName
        avatarUrl
      }
      totalCount
    }
  }
`;
